import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      {!isUser && (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
            mt: 0.5,
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 14, color: '#fff' }} />
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          maxWidth: '82%',
          px: 1.5,
          py: 1,
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          background: isUser ? '#1a1a1a' : '#f5f5f5',
          color: isUser ? '#fff' : '#1a1a1a',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>
        {message.filesGenerated > 0 && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, opacity: 0.6, fontSize: 11 }}
          >
            ✦ {message.filesGenerated} file{message.filesGenerated !== 1 ? 's' : ''} generated
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

const StreamingIndicator = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <AutoAwesomeIcon sx={{ fontSize: 14, color: '#fff' }} />
    </Box>
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#6366f1',
            animation: 'ds-bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes ds-bounce': {
              '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
              '40%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  </Box>
);

/**
 * DesignChat — left-panel chat UI for the Design Studio.
 *
 * Props:
 *   messages: Array<{ role, content, filesGenerated? }>
 *   isStreaming: boolean
 *   streamingText: string — partial assistant response while streaming
 *   onSend: (prompt: string) => void
 *   disabled: boolean
 */
const DesignChat = ({
  messages = [],
  isStreaming = false,
  streamingText = '',
  onSend,
  disabled = false,
}) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming, streamingText]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || disabled || isStreaming) return;
    setInput('');
    onSend?.(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isEmpty = messages.length === 0 && !isStreaming;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
      }}
    >
      {/* Message list */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          pt: 2,
          pb: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: '#e0e0e0', borderRadius: 2 },
        }}
      >
        {isEmpty ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              opacity: 0.5,
              userSelect: 'none',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 40, color: '#6366f1' }} />
            <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', maxWidth: 220 }}>
              Describe a screen or user flow and I'll generate a React prototype
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}
            {isStreaming && streamingText && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 14, color: '#fff' }} />
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: '82%',
                    px: 1.5,
                    py: 1,
                    borderRadius: '16px 16px 16px 4px',
                    background: '#f5f5f5',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {streamingText}
                  </Typography>
                </Paper>
              </Box>
            )}
            {isStreaming && !streamingText && <StreamingIndicator />}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box
        sx={{
          px: 2,
          pb: 2,
          pt: 1,
          borderTop: '1px solid #f0f0f0',
          background: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Describe a screen…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled || isStreaming}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: 13,
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim() || disabled || isStreaming}
            sx={{
              background: '#1a1a1a',
              color: '#fff',
              borderRadius: 2,
              width: 36,
              height: 36,
              flexShrink: 0,
              '&:hover': { background: '#333' },
              '&.Mui-disabled': { background: '#e0e0e0', color: '#aaa' },
            }}
          >
            {isStreaming ? (
              <CircularProgress size={16} sx={{ color: 'inherit' }} />
            ) : (
              <SendIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default DesignChat;
